import { Database } from 'lucide-react';
import React from 'react';
import { Button, Typography } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { AICampaignFormData } from '../types';
const { Title } = Typography;

interface ThirdStepProps {
	formData: AICampaignFormData;
	setFormData: React.Dispatch<React.SetStateAction<AICampaignFormData>>;
	toggleRequired: (id: string) => void;
	removeField: (id: string) => void;
	newFieldName: string;
	setNewFieldName: React.Dispatch<React.SetStateAction<string>>;
	addNewField: () => void;
	handleFieldChange: (id: string, value: string) => void;
}

// Third Step Component
const ThirdStep: React.FC<ThirdStepProps> = ({
	formData,
	setFormData,
	toggleRequired,
	removeField,
	newFieldName,
	setNewFieldName,
	addNewField,
	handleFieldChange,
}) => {
	return (
		<div className="step-content">
			<div className="flex">
				<Database size={36} className="text-blue-600 mr-3" />
				<Title className="my-0" level={2}>
					What data do you have about each lead?
				</Title>
			</div>
			<p className="text-gray-600 mb-6">
				Let us know what information you have for each lead. The more data you provide, the more
				personalized your outreach can be.
			</p>

			{/* Fields Table */}
			<div className="mb-6">
				<div className="grid grid-cols-4 gap-4 mb-3 text-sm font-medium text-gray-500 w-full">
					<div>Field Name</div>
					<div>Required</div>
					<div>Example Value</div>
					<div>Remove</div>
				</div>

				{formData.leadFields?.map((field) => (
					<div key={field.id} className="grid grid-cols-4 gap-4 mb-3 items-center">
						<div>
							<input
								type="text"
								value={field.name}
								onChange={(e) => handleFieldChange(field.id, e.target.value)}
								className="w-full p-2 border border-gray-300 rounded"
								placeholder="Field name"
								readOnly={false}
							/>
						</div>
						<div>
							<button
								onClick={() => toggleRequired(field.id)}
								className={`px-3 py-1 rounded-md text-sm ${
									field.required ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
								}`}
							>
								{field.required ? 'Required' : 'Optional'}
							</button>
						</div>
						<div>
							<input
								type="text"
								value={field.example || ''}
								onChange={(e) => {
									const updatedFields = [...(formData.leadFields || [])];
									const index = updatedFields.findIndex((f) => f.id === field.id);
									updatedFields[index] = { ...field, example: e.target.value };
									setFormData((prevData) => ({
										...prevData,
										leadFields: updatedFields,
									}));
								}}
								className="w-full p-2 border border-gray-300 rounded"
								placeholder="Example value"
							/>
						</div>
						<div>
							<Button
								onClick={() => removeField(field.id)}
								className="text-gray-400 hover:text-gray-600 border-0"
							>
								<CloseOutlined />
							</Button>
						</div>
					</div>
				))}

				{/* Add new field input */}
				<div className="grid grid-cols-5 gap-4 mt-6 items-center">
					<div className="col-span-3">
						<input
							type="text"
							value={newFieldName}
							onChange={(e) => setNewFieldName(e.target.value)}
							className="w-full p-2 border border-gray-300 rounded"
							placeholder="Add a new field (e.g., Industry, Job Title)"
						/>
					</div>
					<div>
						<Button
							onClick={addNewField}
							className="p-2 border border-gray-300 rounded hover:bg-gray-50"
						>
							<PlusOutlined />
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ThirdStep;
